import {Menu} from 'antd';
import moment from 'moment';
import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef, FC, RefObject,
    Key,
} from 'react';

import {customTableUtils} from 'components/@common/widgets/custom-table';
import {useCustomTable, IUseCustomTableArgs} from 'components/@common/widgets/custom-table/custom-table';
import {IColumnSortData} from 'components/@common/widgets/custom-table/table-column-menu';
import {KebabMenu} from 'components/@common/widgets/kebab-menu';
import {IconsMap} from 'components/dynamic-icon';
import {IModalOpenerComponentRef} from 'components/modal-opener-component';
import {Spinner} from 'components/spinner';
import {ViewPudButton} from 'pages/ais/documents-register-page/components/view-pud-button/view-pud-button';
import {DATE_DMY} from 'shared/constants/date-format';
import {useAfterEffect} from 'shared/hooks/use-after-effect';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {usePagination} from 'shared/hooks/use-pagination';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {ITableAttributes} from 'shared/types';
import {TStateSetter} from 'shared/types/generics';
import {formatMonetaryNumber} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {aisSelectors, aisSliceActions, selectAisSliceMeta} from 'store/slices/ais-slice/ais-slice';
import {loadAisDocumentsRegister} from 'store/slices/ais-slice/ais-slice-thunks';
import {EAisDocRegisterFieldTypes, IAisRegisterDocumentDto} from 'store/slices/ais-slice/ais-slice-types';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {AIS_REGISTER_ROLE_SECTION} from './ais-documents-register-constants';
import {
    IDocumentsRegisterParametersFilterForm,
    TDocumentsRegisterRequestQueryRef,
} from './ais-documents-register-types';
import {AisRequestModal} from './modals/ais-request-modal';
import {DocumentCardModal} from './modals/document-card-modal';
import {PlacementRequestModal} from './modals/placement-request-modal';
import {PUDDeleteConfirmationModal} from './modals/pud-delete-confirmation-modal';
import {PUDModal} from './modals/pud-modal';
import {RequireRequestModal} from './modals/require-request-modal';
import {TaxSliceModal} from './modals/tax-slice-modal';

const {
    setHeaderStyles,
    createIndexChild,
} = customTableUtils;

interface IAisDocumentsRegisterTableProps {
    documentsParametersFilter?: IDocumentsRegisterParametersFilterForm;
    selectedRowKeys: Key[];
    setSelectedRowKeys: TStateSetter<Key[]>;
    documentsRequestQueryRef: RefObject<TDocumentsRegisterRequestQueryRef>;
    tableRef: RefObject<HTMLDivElement>;
}
export interface IAisDocumentsRegisterTableRef {
    setSortData: TStateSetter<IColumnSortData>;
}

export const AisDocumentsRegisterTable: FC<IAisDocumentsRegisterTableProps> = (
    {
        documentsParametersFilter,
        selectedRowKeys,
        setSelectedRowKeys,
        documentsRequestQueryRef,
        tableRef,
    }: IAisDocumentsRegisterTableProps,
) => {
    const dispatch = useAppDispatch();
    const history = useAppHistory();

    const {setRegisterDocumentsColumnsFilters} = aisSliceActions;
    const tableColumnsFilters = useAppSelector(aisSelectors.selectAisRegisterDocumentsColumnsFiltersData);
    const tableColumnsFiltersAdaptedForRequest = useAppSelector(
        aisSelectors.selectAisRegisterColumnsFiltersDataAdaptedForRequest,
    );

    const {selectAll: selectAllDocuments} = aisSelectors.documentsSelectors;

    const isLoadingRegisterTableData = useAppSelector(s => selectIsThunkPending(
        s, loadAisDocumentsRegister.typePrefix,
    ));

    const linkFilterLocationState = history.currentState?.[ELocationStateKey.LINK_FILTER_PARAMETERS];

    const documents = useAppSelector(selectAllDocuments);

    const {
        quantityLines: defaultPageSize,
        useParameters,
    } = useAppSelector(aisSelectors.selectAisRegisterDocumentsSettings) ?? {};
    const {columns: rawColumns} = useAppSelector(aisSelectors.selectAisRegisterDocumentsFields) ?? {};

    const shouldLoadWithoutFilters = useParameters === false;

    const requestPlacementModalRef = useRef<IModalOpenerComponentRef>(null);
    const requireRequestsModalRef = useRef<IModalOpenerComponentRef>(null);
    const aisModalRef = useRef<IModalOpenerComponentRef>(null);
    const taxSliceModalRef = useRef<IModalOpenerComponentRef>(null);
    const PUDModalRef = useRef<IModalOpenerComponentRef>(null);
    const PUDDeleteModalRef = useRef<IModalOpenerComponentRef>(null);

    const {hasUserRoleFunction, isLoadingUserFunctions} = useUserRoleFunctions({
        sections: [AIS_REGISTER_ROLE_SECTION],
    });

    const pageSizeValue = defaultPageSize ?? 20;
    // @ts-ignore
    const pageSizeOptions = [...new Set(['5', '10', '20', '50', '100', String(defaultPageSize)])]
        .sort((a, b) => Number(a) - Number(b));

    const {
        current, pageSize, handleChange, setCurrentManually, shouldTriggerPaginationFetch,
    } = usePagination({
        pageDefault: 1,
        pageSizeDefault: pageSizeValue,
    });

    const {documentsTotalCount} = useAppSelector(selectAisSliceMeta);

    const columns: IUseCustomTableArgs['columns'] = [
        {
            title: '',
            render: (value: any, record: any) => (
                <div className="d-flex align-items-center gap-1-25">
                    <KebabMenu
                        button={{
                            type: 'primary',
                            size: 'small',
                        }}
                        placeholder={[
                            EUserRoleFunction.VIEW_REQUESTS,
                            EUserRoleFunction.VIEW_REQUIREMENTS,
                            EUserRoleFunction.UPLOAD_PUD,
                            EUserRoleFunction.DELETE_PUD,
                            EUserRoleFunction.DOC_JOURNAL_TAXES,
                            EUserRoleFunction.VIEW_REQ_4_SCENARIO,
                        ].every(f => !hasUserRoleFunction(f)) ? <>Нет доступных действий</> : undefined}
                    >
                        <Menu>
                            {hasUserRoleFunction(EUserRoleFunction.VIEW_REQUESTS) && (
                                <Menu.Item onClick={() => {
                                    requestPlacementModalRef.current?.showModal();
                                }}
                                >Просмотреть запросы на доразмещение
                                </Menu.Item>
                            )}

                            {hasUserRoleFunction(EUserRoleFunction.VIEW_REQUIREMENTS) && (
                                <Menu.Item onClick={() => {
                                    requireRequestsModalRef.current?.showModal();
                                }}
                                >Просмотреть требования
                                </Menu.Item>
                            )}

                            {hasUserRoleFunction(EUserRoleFunction.UPLOAD_PUD)
                            && !record.contentAvailability
                            && (
                                <Menu.Item
                                    onClick={() => { PUDModalRef.current?.showModal(); }}
                                >Разместить ПУД
                                </Menu.Item>
                            )}

                            {hasUserRoleFunction(EUserRoleFunction.DELETE_PUD)
                            && record.contentAvailability && (
                                <Menu.Item onClick={() => {
                                    PUDDeleteModalRef.current?.showModal();
                                }}
                                >Удалить ПУД
                                </Menu.Item>
                            )}

                            {hasUserRoleFunction(EUserRoleFunction.DOC_JOURNAL_TAXES) && (
                                <Menu.Item onClick={() => {
                                    taxSliceModalRef.current?.showModal();
                                }}
                                >Просмотреть документ в разрезе налогов
                                </Menu.Item>
                            )}

                            {hasUserRoleFunction(EUserRoleFunction.VIEW_REQ_4_SCENARIO) && (
                                <Menu.Item onClick={() => {
                                    aisModalRef.current?.showModal();
                                }}
                                >Просмотреть запросы по 4 сценарию
                                </Menu.Item>
                            )}
                            <PlacementRequestModal
                                registerDocument={record}
                                ref={requestPlacementModalRef}
                            />
                            <RequireRequestModal
                                ref={requireRequestsModalRef}
                                registerDocument={record}
                            />
                            <AisRequestModal
                                ref={aisModalRef}
                                registerDocument={record}
                            />
                            <PUDModal
                                ref={PUDModalRef}
                                registerDocument={record}
                            />
                            <PUDDeleteConfirmationModal
                                ref={PUDDeleteModalRef}
                                registerDocument={record}
                            />
                            <TaxSliceModal
                                ref={taxSliceModalRef}
                                registerDocument={record}
                            />
                        </Menu>
                    </KebabMenu>

                    {hasUserRoleFunction(EUserRoleFunction.VIEW_PUD) && (
                        <ViewPudButton link={record?.fixed_dd_url_config} />
                    )}
                </div>
            ),
        },
    ];

    rawColumns?.forEach(rc => {
        const render = (() => {
            if (rc.fieldKey === 'aisDocId') {
                return (v: string, registerDocumentRecord: IAisRegisterDocumentDto | undefined) => (
                    <DocumentCardModal
                        initialRegisterDocument={registerDocumentRecord}
                        cellValue={v}
                    />
                );
            }

            switch (rc.type) {
            case EAisDocRegisterFieldTypes.MONETARY: {
                return (v?: string) => (
                    <div className="text-center">
                        {formatMonetaryNumber(v)}
                    </div>
                );
            }
            case EAisDocRegisterFieldTypes.DATE:
                return (v: string) => (
                    <div className="text-center">
                        {v && moment(v).format(DATE_DMY)}
                    </div>
                );
            default:
                return undefined;
            }
        })();

        columns.push({
            title: rc.label,
            dataIndex: rc.fieldKey,
            key: rc.fieldKey,
            hideSortDropdown: true,
            render,
        });
    });

    const {
        customTableJSX,
    } = useCustomTable({
        tableFiltersData: tableColumnsFilters,
        setTableFiltersData: columnFilters => {
            dispatch(setRegisterDocumentsColumnsFilters({
                filters: columnFilters,
            }));
        },
        pagination: {
            position: ['bottomLeft'],
            showQuickJumper: {goButton: <IconsMap.ArrowRightIcon className="pagination_jumper" />},
            showSizeChanger: true,
            locale: {items_per_page: 'на странице', jump_to: ''},
            current,
            pageSizeOptions,
            total: (() => {
                if (!isLoadingUserFunctions && !hasUserRoleFunction(EUserRoleFunction.VIEW)) return undefined;
                return documentsTotalCount;
            })(),
            pageSize,
            onChange: handleChange,
        },
        rowKey: 'id',
        rowSelection: {
            selectedRowKeys,
            onChange: selectedTableRowKeys => setSelectedRowKeys(selectedTableRowKeys),
        },
        // ---
        className: 'ais-documents-register-table mt-5',
        locale: {
            emptyText: (
                <div style={{marginTop: 30}}>
                    {(() => {
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && isLoadingUserFunctions) {
                            return <div><Spinner tip="Загрузка..." /></div>;
                        }
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && !isLoadingUserFunctions) {
                            return 'Недостаточно прав для просмотра данных';
                        }
                        if ((documentsParametersFilter || shouldLoadWithoutFilters) && !isLoadingRegisterTableData) {
                            return 'Ничего не найдено';
                        }
                        return 'Для формирования данных необходимо задать параметры';
                    })()}
                </div>
            ),
        },
        dataSource: (() => {
            if (isLoadingUserFunctions) return [];
            if (!isLoadingUserFunctions && !hasUserRoleFunction(EUserRoleFunction.VIEW)) return [];
            if (!documentsParametersFilter && !shouldLoadWithoutFilters) return [];
            return documents;
        })(),
        onColumn: (col, i) => {
            const SKIP_INDEX = 1;
            const ordinal = rawColumns?.find(rc => rc?.fieldKey === col.dataIndex)?.columnOrdinal ?? i;

            return {
                // Включаем отображение фильтрации для всех колонок
                withCustomFilters: i >= SKIP_INDEX,
                ...setHeaderStyles({
                    whiteSpace: 'pre',
                    textAlign: 'center',
                }),
                ...createIndexChild(col.dataIndex?.toString(), `${ordinal}`, ordinal < SKIP_INDEX, {
                    render: col.render ?? (cellValue => <div style={{textAlign: 'center'}}>{cellValue}</div>),
                }),
                ...col,
            };
        },
        bordered: true,
        columns,
        tableAttributes: rawColumns?.map((c): ITableAttributes => ({name: c.fieldKey, type: c.type})),
        selectColumnFilterEntriesByFieldType: aisSelectors.selectAisRegisterDocumentsFilterEntriesByFieldType,
    });

    const documentsRequestQuery = useMemo(() => ({
        ...documentsParametersFilter,
        ...linkFilterLocationState,
        tableParamDto: {
            paginationCurrent: current,
            paginationPageSize: pageSize,
        },
    }), [documentsParametersFilter, current, pageSize]);

    useImperativeHandle(documentsRequestQueryRef, () => ({
        getParams: () => documentsRequestQuery,
    }), [documentsRequestQuery]);

    useEffect(() => {
      if (shouldLoadWithoutFilters) {
            const loadThunk = dispatch(loadAisDocumentsRegister({
                ...documentsRequestQuery,
                filters: tableColumnsFiltersAdaptedForRequest,
            }));
            setCurrentManually(1);
            return () => {
                loadThunk.abort();
            };
        }
        return () => {};
    }, [shouldLoadWithoutFilters, tableColumnsFiltersAdaptedForRequest]);

    useAfterEffect(() => {
        if (documentsParametersFilter) {
            const loadThunk = dispatch(loadAisDocumentsRegister({
                ...documentsRequestQuery,
                filters: tableColumnsFiltersAdaptedForRequest,
            }));

            setCurrentManually(1);

            return () => {
                loadThunk.abort();
            };
        }
        return () => {};
    }, [documentsParametersFilter, tableColumnsFiltersAdaptedForRequest]);

    useAfterEffect(() => {
        if ((documentsParametersFilter && shouldTriggerPaginationFetch) || shouldLoadWithoutFilters) {
            const loadThunk = dispatch(loadAisDocumentsRegister({
                ...documentsRequestQuery,
                filters: tableColumnsFiltersAdaptedForRequest,
            }));

            return () => {
                loadThunk.abort();
            };
        }
        return () => {};
    }, [pageSize, current, tableColumnsFiltersAdaptedForRequest]);

    useEffect(() => () => { dispatch(aisSliceActions.updateAisSliceMeta({documentsTotalCount: undefined})); }, []);

    return <div ref={tableRef}>{customTableJSX}</div>;
};
